import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantViewings } from '../../hooks/useViewings';
import { formatTime, formatDate } from '../../services/viewings.service';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FirebaseStorageUploadService } from '../../services/firebaseStorageUpload';
import { createPropertyApplication, getApplicationsForTenant } from '../../utils/propertyApplications';
import { countTenantNotificationBuckets, mergeNotificationDocs } from '../../utils/tenantNotificationBuckets';
// getUnreadNotifications replaced by real-time listener
import { 
  FileText, 
  Calendar, 
  User, 
  ChevronLeft,
  LogOut,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  Phone,
  Edit3,
  Settings,
  Bell,
  Shield,
  ArrowRight,
  Sparkles,
  Building,
  Save,
  Camera,
  X,
  GraduationCap,
  Home,
  Globe,
  Cake,
  Briefcase,
  Heart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export default function AccountOverviewScreen() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState('profile');
  const { viewings, loading: viewingsLoading } = useTenantViewings();
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const containerRef = useRef(null);
  const motivationFileRef = useRef(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    phone: '',
    birthDate: '',
    address: {
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: 'België'
    },
    education: {
      university: '',
      fieldOfStudy: '',
      studyYear: '',
      degree: ''
    },
    nationality: '',
    bio: '',
    preferences: {
      smoking: false,
      pets: false,
      petsDescription: '',
      noise: 'normal' // quiet, normal, loud
    },
    profileImageUrl: ''
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [applicationsByViewingId, setApplicationsByViewingId] = useState({});
  const [uploadingViewingId, setUploadingViewingId] = useState(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [unreadNotificationCounts, setUnreadNotificationCounts] = useState({
    contracts: 0,
    viewings: 0,
    mijnPand: 0
  });

  // Load user profile
  useEffect(() => {
    if (user) {
      loadUserProfile();
      loadContracts();
    }
  }, [user]);

  useEffect(() => {
    if (user?.uid) {
      loadMotivationApplications();
    } else {
      setApplicationsByViewingId({});
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setUnreadNotificationCounts({ contracts: 0, viewings: 0, mijnPand: 0 });
      return;
    }

    const recipientQuery = query(
      collection(db, 'notifications'),
      where('recipientUserId', '==', user.uid),
      where('read', '==', false)
    );
    const tenantQuery = query(
      collection(db, 'notifications'),
      where('tenantId', '==', user.uid),
      where('read', '==', false)
    );

    let recipientDocs = [];
    let tenantDocs = [];

    const updateCounts = () => {
      const notifications = mergeNotificationDocs(recipientDocs, tenantDocs).map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setUnreadNotificationCounts(countTenantNotificationBuckets(notifications));
    };

    const unsubRecipient = onSnapshot(recipientQuery, (snapshot) => {
      recipientDocs = snapshot.docs;
      updateCounts();
    }, () => {
      recipientDocs = [];
      updateCounts();
    });

    const unsubTenant = onSnapshot(tenantQuery, (snapshot) => {
      tenantDocs = snapshot.docs;
      updateCounts();
    }, () => {
      tenantDocs = [];
      updateCounts();
    });

    return () => {
      unsubRecipient();
      unsubTenant();
    };
  }, [user?.uid]);
  
  const loadContracts = async () => {
    if (!user) return;
    
    try {
      setLoadingContracts(true);
      
      // Try both tenantID and tenantId fields for backwards compatibility
      const contractsQuery1 = query(
        collection(db, 'contracts'),
        where('tenantID', '==', user.uid)
      );
      
      const contractsQuery2 = query(
        collection(db, 'contracts'),
        where('tenantId', '==', user.uid)
      );
      
      // Execute both queries
      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(contractsQuery1).catch(err => {
          console.log('Query 1 (tenantID) failed:', err);
          return { docs: [] };
        }),
        getDocs(contractsQuery2).catch(err => {
          console.log('Query 2 (tenantId) failed:', err);
          return { docs: [] };
        })
      ]);
      
      // Combine results and remove duplicates
      const allDocs = [...snapshot1.docs, ...snapshot2.docs];
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.id === doc.id)
      );
      
      // Map to data and filter by status client-side
      const allContracts = uniqueDocs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter by status - show sentToTenant, signedByTenant, and fullySigned contracts
      const validStatuses = ['fullySigned', 'signedByTenant', 'sentToTenant', 'signedByLandlord'];
      const contractsData = allContracts.filter(c => 
        validStatuses.includes(c.status)
      );
      
      console.log(`✅ Found ${contractsData.length} contracts for tenant ${user.uid} (out of ${allContracts.length} total)`);
      
      setContracts(contractsData);
    } catch (error) {
      console.error('Error loading contracts:', error);
      toast.error('Fout bij het laden van contracten');
    } finally {
      setLoadingContracts(false);
    }
  };

  const loadMotivationApplications = async () => {
    try {
      const apps = await getApplicationsForTenant(user.uid);
      const map = {};
      apps.forEach(app => {
        if (app.viewingId) {
          map[app.viewingId] = app;
        }
      });
      setApplicationsByViewingId(map);
    } catch (error) {
      console.error('Error loading motivation applications:', error);
    }
  };

  const loadUserProfile = async () => {
    try {
      setLoadingProfile(true);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserProfile(data);
        // Initialize form data with existing profile data
        setProfileFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          displayName: data.displayName || '',
          email: data.email || user?.email || '',
          phone: data.phone || '',
          birthDate: data.birthDate || '',
          address: {
            street: data.address?.street || '',
            houseNumber: data.address?.houseNumber || '',
            postalCode: data.address?.postalCode || '',
            city: data.address?.city || '',
            country: data.address?.country || 'België'
          },
          education: {
            university: data.education?.university || '',
            fieldOfStudy: data.education?.fieldOfStudy || '',
            studyYear: data.education?.studyYear || '',
            degree: data.education?.degree || ''
          },
          nationality: data.nationality || '',
          bio: data.bio || '',
          preferences: {
            smoking: data.preferences?.smoking || false,
            pets: data.preferences?.pets || false,
            petsDescription: data.preferences?.petsDescription || '',
            noise: data.preferences?.noise || 'normal'
          },
          profileImageUrl: data.profileImageUrl || ''
        });
        setProfilePhotoPreview(data.profileImageUrl || null);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Fout bij het laden van profiel');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleMotivationUploadClick = (viewingId) => {
    if (!motivationFileRef.current) return;
    motivationFileRef.current.dataset.viewingId = viewingId;
    motivationFileRef.current.click();
  };

  const handleMotivationUpload = async (event) => {
    const file = event.target.files?.[0];
    const viewingId = event.target.dataset.viewingId;
    event.target.value = '';

    if (!file || !viewingId || !user?.uid) return;

    if (applicationsByViewingId[viewingId]) {
      toast.error('Je hebt al een motivatiebrief ingediend voor deze bezichtiging');
      return;
    }

    const viewing = viewings.find(v => v.id === viewingId);
    if (!viewing) {
      toast.error('Bezichtiging niet gevonden');
      return;
    }

    try {
      setUploadingViewingId(viewingId);
      const propertyId = viewing.listingId || viewing.propertyId || viewing.kotID;
      if (!propertyId) {
        throw new Error('Geen pand gekoppeld aan deze bezichtiging');
      }

      const folder = `motivations/${propertyId}`;
      const fileName = `motivation_${user.uid}_${viewingId}_${Date.now()}.pdf`;
      const uploadResult = await FirebaseStorageUploadService.uploadDocument(file, folder, fileName);

      const tenantSnapshot = {
        displayName: user.displayName || user.name || '',
        email: user.email || '',
        uid: user.uid
      };

      await createPropertyApplication({
        propertyId,
        viewingId,
        tenantId: user.uid,
        motivationLetterUrl: uploadResult.url,
        motivationLetterPath: uploadResult.path,
        tenantSnapshot
      });

      toast.success('Motivatiebrief succesvol ingediend');
      await loadMotivationApplications();
    } catch (error) {
      console.error('Error uploading motivation letter:', error);
      toast.error(error.message || 'Upload mislukt');
    } finally {
      setUploadingViewingId(null);
    }
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Alleen afbeeldingen zijn toegestaan (JPEG, PNG, GIF)');
      e.target.value = ''; // Reset input
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Afbeelding is te groot. Maximum 5MB toegestaan.');
      e.target.value = ''; // Reset input
      return;
    }

    console.log('Profile photo selected:', file.name, file.size, file.type);
    
    setProfilePhotoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePhotoPreview(reader.result);
      console.log('Preview created');
    };
    reader.onerror = () => {
      console.error('Error reading file');
      toast.error('Fout bij lezen van bestand');
    };
    reader.readAsDataURL(file);
  };

  const handleProfileInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setProfileFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setProfileFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      
      let profileImageUrl = profileFormData.profileImageUrl;
      
      // Upload profile photo if a new one was selected
      if (profilePhotoFile) {
        setUploadingPhoto(true);
        toast.loading('Foto wordt geüpload...', { id: 'upload-photo' });
        try {
          console.log('Starting photo upload...', {
            fileName: profilePhotoFile.name,
            fileSize: profilePhotoFile.size,
            fileType: profilePhotoFile.type
          });
          
          // Upload to Firebase Storage
          const folder = `profiles/${user.uid}`;
          const fileName = `profile_${Date.now()}.${profilePhotoFile.name.split('.').pop() || 'jpg'}`;
          
          console.log('Attempting Firebase Storage upload with:', {
            folder,
            fileName
          });
          
          const result = await FirebaseStorageUploadService.uploadPhoto(profilePhotoFile, folder, fileName);
          
          console.log('Photo upload successful:', result.url);
          profileImageUrl = result.url;
          
          toast.success('Foto succesvol geüpload', { id: 'upload-photo' });
        } catch (error) {
          console.error('Error uploading photo:', error);
          toast.error(`Fout bij uploaden foto: ${error.message || 'Onbekende fout'}`, { id: 'upload-photo', duration: 5000 });
          setUploadingPhoto(false);
          return;
        } finally {
          setUploadingPhoto(false);
        }
      }

      // Update displayName if firstName or lastName changed
      const displayName = profileFormData.firstName && profileFormData.lastName
        ? `${profileFormData.firstName} ${profileFormData.lastName}`
        : profileFormData.displayName || user?.displayName || '';

      // Prepare update data
      const updateData = {
        firstName: profileFormData.firstName,
        lastName: profileFormData.lastName,
        displayName: displayName,
        phone: profileFormData.phone,
        birthDate: profileFormData.birthDate,
        address: profileFormData.address,
        education: profileFormData.education,
        nationality: profileFormData.nationality,
        bio: profileFormData.bio,
        preferences: profileFormData.preferences,
        profileImageUrl: profileImageUrl,
        updatedAt: new Date()
      };

      // Update Firestore
      await updateDoc(doc(db, 'users', user.uid), updateData);
      
      // Update local state
      setUserProfile(prev => ({ ...prev, ...updateData }));
      setProfileFormData(prev => ({ ...prev, profileImageUrl: profileImageUrl }));
      setProfilePhotoFile(null);
      
      toast.success('Profiel succesvol bijgewerkt!');
      setIsEditingProfile(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Fout bij opslaan profiel. Probeer het opnieuw.');
    } finally {
      setSavingProfile(false);
    }
  };


  // Tenant view types
  const tenantViewTypes = [
    { 
      id: 'mijn-pand', 
      label: 'Mijn Pand', 
      icon: Building,
      description: 'Beheer je huurpand en contract',
      gradient: 'from-orange-500 to-orange-600',
      count: null,
      notificationCount: unreadNotificationCounts.mijnPand,
      path: '/mijn-pand'
    },
    { 
      id: 'contracts', 
      label: 'Contracten', 
      icon: FileText,
      description: 'Bekijk je huurcontracten',
      gradient: 'from-blue-500 to-blue-600',
      count: 0,
      notificationCount: unreadNotificationCounts.contracts
    },
    { 
      id: 'viewings', 
      label: 'Bezichtigingen', 
      icon: Calendar,
      description: 'Beheer je bezichtigingen',
      gradient: 'from-green-500 to-green-600',
      count: null,
      notificationCount: unreadNotificationCounts.viewings
    },
    { 
      id: 'profile', 
      label: 'Profiel', 
      icon: User,
      description: 'Bewerk je persoonlijke gegevens',
      gradient: 'from-purple-500 to-purple-600',
      count: null,
      notificationCount: 0
    }
  ];

  const handleLogout = async () => {
    if (window.confirm('Weet je zeker dat je wilt uitloggen?')) {
      await logout();
      toast.success('Succesvol uitgelogd');
    }
  };

  // Get user initials
  const getUserInitials = () => {
    if (userProfile?.displayName) {
      const parts = userProfile.displayName.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0][0]?.toUpperCase() || '?';
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { 
        icon: AlertCircle, 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
        label: 'In afwachting',
        iconColor: 'text-yellow-600'
      },
      accepted: {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-800 border-green-200',
        label: 'Bevestigd',
        iconColor: 'text-green-600'
      },
      confirmed: { 
        icon: CheckCircle, 
        color: 'bg-green-100 text-green-800 border-green-200', 
        label: 'Bevestigd',
        iconColor: 'text-green-600'
      },
      declined: { 
        icon: XCircle, 
        color: 'bg-red-100 text-red-800 border-red-200', 
        label: 'Afgewezen',
        iconColor: 'text-red-600'
      },
      cancelled: { 
        icon: XCircle, 
        color: 'bg-gray-100 text-gray-800 border-gray-200', 
        label: 'Geannuleerd',
        iconColor: 'text-gray-600'
      },
      completed: { 
        icon: CheckCircle, 
        color: 'bg-blue-100 text-blue-800 border-blue-200', 
        label: 'Voltooid',
        iconColor: 'text-blue-600'
      },
      no_show: { 
        icon: AlertCircle, 
        color: 'bg-orange-100 text-orange-800 border-orange-200', 
        label: 'Niet verschenen',
        iconColor: 'text-orange-600'
      }
    };
    return badges[status] || badges.pending;
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50"
    >
      <input
        ref={motivationFileRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleMotivationUpload}
      />
      {/* Hero Section */}
      {!selectedView && (
        <div className="relative bg-gradient-to-br from-ios-blue via-blue-600 to-purple-600 overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full animate-float-slow"
              style={{
                animationDuration: '25s',
                animationDelay: '0s'
              }}
            ></div>
            <div
              className="absolute top-1/2 -left-32 w-64 h-64 bg-white/10 rounded-full animate-float-gentle"
              style={{
                animationDuration: '20s',
                animationDelay: '2s'
              }}
            ></div>
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute bg-white/5 rounded-full animate-float-gentle"
                style={{
                  width: `${30 + Math.random() * 50}px`,
                  height: `${30 + Math.random() * 50}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDuration: `${15 + Math.random() * 10}s`,
                  animationDelay: `${Math.random() * 3}s`
                }}
              ></div>
            ))}
          </div>

          <div className="relative z-10 container-professional py-12 md:py-16">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center shadow-xl border-2 border-white/30">
                    {userProfile?.profileImageUrl ? (
                      <img 
                        src={userProfile.profileImageUrl} 
                        alt={userProfile.displayName || 'User'}
                        className="w-full h-full rounded-3xl object-cover"
                      />
                    ) : (
                      <span className="text-3xl font-bold text-white">
                        {getUserInitials()}
                      </span>
                    )}
                  </div>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
                  {userProfile?.displayName || user?.displayName || 'Mijn Account'}
                </h1>
                <p className="text-lg text-blue-100 mb-6">
                  {userProfile?.email || user?.email || 'Geen e-mail'}
                </p>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 border border-white/30"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Uitloggen</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="container-professional py-8">
          <div className="max-w-4xl mx-auto">

            {selectedView === 'contracts' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl shadow-lg p-8 text-white mb-8">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <FileText className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold mb-2">Contracten</h2>
                      <p className="text-blue-100">Bekijk al je huurcontracten</p>
                    </div>
                  </div>
                </div>

                {loadingContracts ? (
                  <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Contracten laden...</p>
                    </div>
                  </div>
                ) : contracts.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-12">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6">
                        <FileText className="w-12 h-12 text-blue-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Geen contracten</h3>
                      <p className="text-gray-600 max-w-md mx-auto mb-6">
                        Je hebt nog geen contracten. Contracten worden hier getoond zodra je een kot huurt.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contracts.map(contract => {
                      const statusColors = {
                        'sentToTenant': 'bg-orange-100 text-orange-800',
                        'signedByTenant': 'bg-blue-100 text-blue-800',
                        'signedByLandlord': 'bg-purple-100 text-purple-800',
                        'fullySigned': 'bg-green-100 text-green-800'
                      };
                      const statusLabels = {
                        'sentToTenant': 'Wacht op ondertekening',
                        'signedByTenant': 'Getekend door jou',
                        'signedByLandlord': 'Getekend door verhuurder',
                        'fullySigned': 'Volledig getekend'
                      };
                      
                      return (
                        <div key={contract.id} className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6 hover:shadow-xl transition-shadow">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 mb-1">
                                {contract.propertyTitle || contract.contractNumber || 'Contract'}
                              </h3>
                              <p className="text-sm text-gray-600">
                                Contractnummer: {contract.contractNumber || contract.id}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColors[contract.status] || 'bg-gray-100 text-gray-800'}`}>
                              {statusLabels[contract.status] || contract.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Maandelijkse huur</p>
                              <p className="font-semibold text-gray-900">€{contract.monthlyRent || contract.core?.rent || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Waarborg</p>
                              <p className="font-semibold text-gray-900">€{contract.deposit || contract.core?.deposit || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Startdatum</p>
                              <p className="font-semibold text-gray-900">
                                {(() => {
                                  const date = contract.startDate?.toDate ? contract.startDate.toDate() :
                                               (contract.startDate instanceof Date ? contract.startDate :
                                               (contract.core?.startDate?.toDate ? contract.core.startDate.toDate() :
                                               (contract.core?.startDate instanceof Date ? contract.core.startDate : null)));
                                  return date ? date.toLocaleDateString('nl-NL') : 'N/A';
                                })()}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Einddatum</p>
                              <p className="font-semibold text-gray-900">
                                {(() => {
                                  const date = contract.endDate?.toDate ? contract.endDate.toDate() :
                                               (contract.endDate instanceof Date ? contract.endDate :
                                               (contract.core?.endDate?.toDate ? contract.core.endDate.toDate() :
                                               (contract.core?.endDate instanceof Date ? contract.core.endDate : null)));
                                  return date ? date.toLocaleDateString('nl-NL') : 'N/A';
                                })()}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                            <p className="text-sm text-gray-600">
                              {contract.createdAt?.toDate ? `Aangemaakt: ${contract.createdAt.toDate().toLocaleDateString('nl-NL')}` : ''}
                            </p>
                            <button
                              onClick={() => navigate(`/contract/${contract.id}`)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                            >
                              <FileText className="w-4 h-4" />
                              <span>Bekijk Contract</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            {selectedView === 'viewings' && (
              <div className="space-y-6" data-tutorial="viewings-section">
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl shadow-lg p-8 text-white mb-8">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Calendar className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold mb-2">Bezichtigingen</h2>
                      <p className="text-green-100">Beheer al je bezichtigingsaanvragen</p>
                    </div>
                  </div>
                </div>

                {viewingsLoading ? (
                  <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-12">
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ios-blue mb-4"></div>
                      <p className="text-gray-600 font-medium">Bezichtigingen laden...</p>
                    </div>
                  </div>
                ) : viewings.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-12">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Calendar className="w-12 h-12 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Geen bezichtigingen</h3>
                      <p className="text-gray-600 max-w-md mx-auto">
                        Je hebt nog geen bezichtigingen aangevraagd. Vraag een bezichtiging aan voor koten die je interessant vindt.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {viewings.map((viewing) => {
                      const startAt = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
                      const endAt = viewing.endAt?.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);
                      const statusInfo = getStatusBadge(viewing.status);
                      const StatusIcon = statusInfo.icon;

                      return (
                        <div 
                          key={viewing.id} 
                          className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 hover:border-ios-blue p-6 transition-all duration-300 transform hover:-translate-y-1"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <div className={`w-10 h-10 bg-gradient-to-br ${statusInfo.color.split(' ')[0]} rounded-xl flex items-center justify-center border-2 ${statusInfo.color.split(' ')[2]}`}>
                                  <StatusIcon className={`w-5 h-5 ${statusInfo.iconColor}`} />
                                </div>
                                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${statusInfo.color} border ${statusInfo.color.split(' ')[2]}`}>
                                  {statusInfo.label}
                                </span>
                              </div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {viewing.listingId ? `Bezichtiging #${viewing.id.slice(-6)}` : 'Bezichtiging'}
                              </h3>
                            </div>
                          </div>

                          <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                            <div className="flex items-center text-gray-700">
                              <Calendar className="w-5 h-5 mr-3 text-ios-blue" />
                              <span className="font-medium">{formatDate(startAt)}</span>
                            </div>
                            
                            <div className="flex items-center text-gray-700">
                              <Clock className="w-5 h-5 mr-3 text-ios-blue" />
                              <span className="font-medium">
                                {formatTime(startAt)} - {formatTime(endAt)}
                              </span>
                            </div>

                            {viewing.location && (
                              <div className="flex items-center text-gray-700">
                                <MapPin className="w-5 h-5 mr-3 text-ios-blue" />
                                <span className="font-medium">{viewing.location}</span>
                              </div>
                            )}

                            {viewing.notes && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-sm text-gray-600">
                                  <span className="font-semibold text-gray-900">Opmerkingen:</span> {viewing.notes}
                                </p>
                              </div>
                            )}
                          </div>

                          {(() => {
                            const application = applicationsByViewingId[viewing.id];
                            const disallowedStatuses = ['declined', 'cancelled', 'no_show'];
                            const canUpload = !disallowedStatuses.includes(viewing.status);

                            if (application) {
                              return (
                                <div className="mt-4 p-4 bg-white border border-gray-200 rounded-xl">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">Motivatiebrief ingediend</p>
                                      <p className="text-xs text-gray-600">
                                        Status: {application.status?.replace('_', ' ') || 'submitted'}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => window.open(application.motivationLetterUrl, '_blank')}
                                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                    >
                                      Bekijk brief
                                    </button>
                                  </div>
                                </div>
                              );
                            }

                            if (canUpload) {
                              return (
                                <div className="mt-4 p-4 bg-white border border-gray-200 rounded-xl">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">Motivatiebrief indienen</p>
                                      <p className="text-xs text-gray-600">Je kan dit al voor de bezichtiging doen (PDF)</p>
                                    </div>
                                    <button
                                      onClick={() => handleMotivationUploadClick(viewing.id)}
                                      disabled={uploadingViewingId === viewing.id}
                                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                                    >
                                      {uploadingViewingId === viewing.id ? 'Uploaden...' : 'Upload'}
                                    </button>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div className="mt-4 p-4 bg-white border border-gray-200 rounded-xl">
                                <p className="text-sm text-gray-600">
                                  Motivatiebrief kan niet ingediend worden voor deze status.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            {selectedView === 'profile' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl shadow-lg p-8 text-white mb-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <User className="w-8 h-8" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold mb-2">Mijn Profiel</h2>
                        <p className="text-purple-100">Bewerk je persoonlijke gegevens</p>
                      </div>
                    </div>
                    {!isEditingProfile && (
                      <button
                        onClick={() => setIsEditingProfile(true)}
                        className="flex items-center space-x-2 px-6 py-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 border border-white/30"
                      >
                        <Edit3 className="w-5 h-5" />
                        <span>Bewerken</span>
                      </button>
                    )}
                  </div>
                </div>

                {isEditingProfile ? (
                  /* Edit Profile Form */
                  <div className="space-y-6">
                    {/* Profile Photo Upload */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <Camera className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Profielfoto</h3>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="relative w-32 h-32">
                          <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-gray-200 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            {profilePhotoPreview ? (
                              <img src={profilePhotoPreview} alt="Profile preview" className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-16 h-16 text-gray-400" />
                            )}
                          </div>
                          {uploadingPhoto && (
                            <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center z-10">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <label htmlFor="profile-photo-input" className="block mb-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleProfilePhotoChange}
                              className="hidden"
                              id="profile-photo-input"
                              disabled={uploadingPhoto}
                            />
                            <div className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-ios-blue to-blue-600 text-white rounded-xl font-semibold cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed">
                              {uploadingPhoto ? (
                                <>
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                  <span>Uploaden...</span>
                                </>
                              ) : (
                                <>
                                  <Camera className="w-5 h-5" />
                                  <span>Foto kiezen</span>
                                </>
                              )}
                            </div>
                          </label>
                          <p className="text-sm text-gray-600 mt-2">JPEG, PNG of GIF. Max 5MB</p>
                          {profilePhotoFile && (
                            <p className="text-sm text-green-600 mt-1">
                              ✓ {profilePhotoFile.name} geselecteerd
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Personal Information */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Persoonlijke informatie</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Voornaam *
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            value={profileFormData.firstName}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="Jouw voornaam"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Achternaam *
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            value={profileFormData.lastName}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="Jouw achternaam"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            E-mailadres
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="email"
                              name="email"
                              value={profileFormData.email}
                              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50"
                              readOnly
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Telefoonnummer
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="tel"
                              name="phone"
                              value={profileFormData.phone}
                              onChange={handleProfileInputChange}
                              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                              placeholder="+32 123 45 67 89"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Geboortedatum
                          </label>
                          <div className="relative">
                            <Cake className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="date"
                              name="birthDate"
                              value={profileFormData.birthDate}
                              onChange={handleProfileInputChange}
                              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Nationaliteit
                          </label>
                          <div className="relative">
                            <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="text"
                              name="nationality"
                              value={profileFormData.nationality}
                              onChange={handleProfileInputChange}
                              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                              placeholder="Bijv. Belgisch"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                          <Home className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Adresgegevens</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Straat
                          </label>
                          <input
                            type="text"
                            name="address.street"
                            value={profileFormData.address.street}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="Kerkstraat"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Huisnummer
                          </label>
                          <input
                            type="text"
                            name="address.houseNumber"
                            value={profileFormData.address.houseNumber}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="123"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Postcode
                          </label>
                          <input
                            type="text"
                            name="address.postalCode"
                            value={profileFormData.address.postalCode}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="8000"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Stad
                          </label>
                          <input
                            type="text"
                            name="address.city"
                            value={profileFormData.address.city}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="Brugge"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Land
                          </label>
                          <input
                            type="text"
                            name="address.country"
                            value={profileFormData.address.country}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="België"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Education Information */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                          <GraduationCap className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Opleiding</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Universiteit / Hogeschool
                          </label>
                          <input
                            type="text"
                            name="education.university"
                            value={profileFormData.education.university}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="Bijv. KU Leuven"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Studierichting
                          </label>
                          <input
                            type="text"
                            name="education.fieldOfStudy"
                            value={profileFormData.education.fieldOfStudy}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="Bijv. Informatica"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Studiejaar
                          </label>
                          <select
                            name="education.studyYear"
                            value={profileFormData.education.studyYear}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                          >
                            <option value="">Selecteer jaar</option>
                            <option value="1">1e Bachelor</option>
                            <option value="2">2e Bachelor</option>
                            <option value="3">3e Bachelor</option>
                            <option value="1m">1e Master</option>
                            <option value="2m">2e Master</option>
                            <option value="phd">PhD</option>
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Diploma
                          </label>
                          <input
                            type="text"
                            name="education.degree"
                            value={profileFormData.education.degree}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                            placeholder="Bijv. Bachelor of Science"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Preferences */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                          <Heart className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Voorkeuren</h3>
                      </div>
                      <div className="space-y-5">
                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
                          <input
                            type="checkbox"
                            name="preferences.smoking"
                            checked={profileFormData.preferences.smoking}
                            onChange={handleProfileInputChange}
                            className="w-5 h-5 text-ios-blue rounded focus:ring-ios-blue cursor-pointer"
                            id="smoking"
                          />
                          <label htmlFor="smoking" className="flex-1 cursor-pointer">
                            <span className="font-semibold text-gray-900">Roken</span>
                            <p className="text-sm text-gray-600">Ik rook</p>
                          </label>
                        </div>
                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
                          <input
                            type="checkbox"
                            name="preferences.pets"
                            checked={profileFormData.preferences.pets}
                            onChange={handleProfileInputChange}
                            className="w-5 h-5 text-ios-blue rounded focus:ring-ios-blue cursor-pointer"
                            id="pets"
                          />
                          <label htmlFor="pets" className="flex-1 cursor-pointer">
                            <span className="font-semibold text-gray-900">Huisdieren</span>
                            <p className="text-sm text-gray-600">Ik heb huisdieren</p>
                          </label>
                        </div>
                        {profileFormData.preferences.pets && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Beschrijving huisdieren
                            </label>
                            <textarea
                              name="preferences.petsDescription"
                              value={profileFormData.preferences.petsDescription}
                              onChange={handleProfileInputChange}
                              rows={3}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                              placeholder="Bijv. 1 kat, 1 hond..."
                            />
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Geluidsniveau voorkeur
                          </label>
                          <select
                            name="preferences.noise"
                            value={profileFormData.preferences.noise}
                            onChange={handleProfileInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                          >
                            <option value="quiet">Stil</option>
                            <option value="normal">Normaal</option>
                            <option value="loud">Geen probleem met geluid</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Bio */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                          <Briefcase className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Over mij</h3>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Biografie
                        </label>
                        <textarea
                          name="bio"
                          value={profileFormData.bio}
                          onChange={handleProfileInputChange}
                          rows={5}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ios-blue focus:border-ios-blue transition-all"
                          placeholder="Vertel wat over jezelf..."
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          {profileFormData.bio.length}/500 karakters
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end space-x-4">
                      <button
                        onClick={() => {
                          setIsEditingProfile(false);
                          loadUserProfile(); // Reset form
                        }}
                        className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300"
                      >
                        Annuleren
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        disabled={savingProfile}
                        className="px-6 py-3 bg-gradient-to-r from-ios-blue to-blue-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {savingProfile ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>Opslaan...</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-5 h-5" />
                            <span>Opslaan</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View Profile Mode */
                  <div className="space-y-6">
                    {/* Profile Photo */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                      <div className="flex items-center justify-center">
                        <div className="w-48 h-48 rounded-3xl overflow-hidden border-4 border-gray-200 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                          {userProfile?.profileImageUrl ? (
                            <img src={userProfile.profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-6xl font-bold text-gray-400">
                              {getUserInitials()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Personal Information Display */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Persoonlijke informatie</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-500 mb-1">Naam</label>
                          <p className="text-lg font-semibold text-gray-900">
                            {userProfile?.displayName || user?.displayName || 'Niet opgegeven'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-500 mb-1">E-mailadres</label>
                          <p className="text-lg text-gray-900">
                            {userProfile?.email || user?.email || 'Niet opgegeven'}
                          </p>
                        </div>
                        {userProfile?.phone && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-500 mb-1">Telefoonnummer</label>
                            <p className="text-lg text-gray-900">{userProfile.phone}</p>
                          </div>
                        )}
                        {userProfile?.birthDate && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-500 mb-1">Geboortedatum</label>
                            <p className="text-lg text-gray-900">
                              {new Date(userProfile.birthDate).toLocaleDateString('nl-NL')}
                            </p>
                          </div>
                        )}
                        {userProfile?.nationality && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-500 mb-1">Nationaliteit</label>
                            <p className="text-lg text-gray-900">{userProfile.nationality}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Address Display */}
                    {(userProfile?.address?.street || userProfile?.address?.city) && (
                      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                            <Home className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">Adres</h3>
                        </div>
                        <p className="text-lg text-gray-900">
                          {[
                            userProfile.address?.street,
                            userProfile.address?.houseNumber,
                            userProfile.address?.postalCode,
                            userProfile.address?.city,
                            userProfile.address?.country
                          ].filter(Boolean).join(' ')}
                        </p>
                      </div>
                    )}

                    {/* Education Display */}
                    {(userProfile?.education?.university || userProfile?.education?.fieldOfStudy) && (
                      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                            <GraduationCap className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">Opleiding</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          {userProfile.education?.university && (
                            <div>
                              <label className="block text-sm font-semibold text-gray-500 mb-1">Universiteit</label>
                              <p className="text-lg text-gray-900">{userProfile.education.university}</p>
                            </div>
                          )}
                          {userProfile.education?.fieldOfStudy && (
                            <div>
                              <label className="block text-sm font-semibold text-gray-500 mb-1">Studierichting</label>
                              <p className="text-lg text-gray-900">{userProfile.education.fieldOfStudy}</p>
                            </div>
                          )}
                          {userProfile.education?.studyYear && (
                            <div>
                              <label className="block text-sm font-semibold text-gray-500 mb-1">Studiejaar</label>
                              <p className="text-lg text-gray-900">{userProfile.education.studyYear}</p>
                            </div>
                          )}
                          {userProfile.education?.degree && (
                            <div>
                              <label className="block text-sm font-semibold text-gray-500 mb-1">Diploma</label>
                              <p className="text-lg text-gray-900">{userProfile.education.degree}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Bio Display */}
                    {userProfile?.bio && (
                      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                            <Briefcase className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">Over mij</h3>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{userProfile.bio}</p>
                      </div>
                    )}

                    {/* Account actions */}
                    <div className="bg-white rounded-2xl shadow-lg border-2 border-red-200 p-8">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                          <AlertCircle className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">Account verwijderen</h3>
                          <p className="text-sm text-gray-600">Deze actie is definitief.</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/delete-account')}
                        className="px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors"
                      >
                        Verwijder account
                      </button>
                    </div>

                    {/* Preferences Display */}
                    {(userProfile?.preferences?.smoking || userProfile?.preferences?.pets || userProfile?.preferences?.noise) && (
                      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                            <Heart className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">Voorkeuren</h3>
                        </div>
                        <div className="space-y-3">
                          {userProfile.preferences?.smoking && (
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="text-gray-700">Roker</span>
                            </div>
                          )}
                          {userProfile.preferences?.pets && (
                            <div>
                              <div className="flex items-center space-x-2 mb-2">
                                <CheckCircle className="w-5 h-5 text-green-500" />
                                <span className="text-gray-700">Heeft huisdieren</span>
                              </div>
                              {userProfile.preferences?.petsDescription && (
                                <p className="text-gray-600 ml-7">{userProfile.preferences.petsDescription}</p>
                              )}
                            </div>
                          )}
                          {userProfile.preferences?.noise && (
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-700">
                                Geluidsniveau voorkeur: {userProfile.preferences.noise === 'quiet' ? 'Stil' : userProfile.preferences.noise === 'normal' ? 'Normaal' : 'Geen probleem met geluid'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(-5px) rotate(-1deg); }
        }

        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-20px) translateX(10px); }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 20s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
